import {Component, Input, OnInit} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {Map} from 'leaflet';
import {MapLayersService} from '../../../shared/map-layers.service';
import {geofenceType} from '../../../shared/constants/geofence-type.constant';
import {GeofenceHelper} from '../../../shared/utils/geofence-helper';
import {GeoItemId} from '../../../shared/types/geo-item-id.type';

const GEO_ITEM_TOGGLE_STORAGE_KEY = 'dashboard-geoitem-toggle';
type ItemValues = Record<GeoItemId, boolean>;

interface GeoItem {
  id: GeoItemId;
  iconClass: string;
  caption: string;
}

@Component({
  selector: 'app-geoitem-toggle',
  templateUrl: './geoitem-toggle.component.html',
  styleUrls: ['./geoitem-toggle.component.scss']
})
export class GeoitemToggleComponent implements OnInit {
  @Input() map: Map;
  @Input() visibleItems: GeoItemId[] = [];
  @Input() useStorage = true;
  filteredItems: GeoItem[] = [];
  geofenceTypeCountBlockVisibility = false;
  items: GeoItem[] = [
    {id: 'regions', iconClass: 'icon-region', caption: 'Regions'},
    {id: 'sum_of_geofences', iconClass: 'ic_multipolygon', caption: 'Sum of geofences'},
    {id: 'demand', iconClass: 'ic_demand_dark', caption: 'Demand assessment areas'},
    {id: 'tech_work_area', iconClass: 'icon-tech_work-area', caption: 'Technics territories'},
    {id: 'geopoints', iconClass: 'icon-geopoint', caption: 'Geopoints'},
    {id: 'geofences', iconClass: 'icon-geofence', caption: 'Geofences'},
    {id: geofenceType.RIDE_ALLOW, iconClass: GeofenceHelper.getIconLayer(geofenceType.RIDE_ALLOW), caption: 'Riding allowed'},
    {id: geofenceType.RIDE_RESTRICT, iconClass: GeofenceHelper.getIconLayer(geofenceType.RIDE_RESTRICT), caption: 'Riding prohibited'},
    {id: geofenceType.FINISH_ALLOW, iconClass: GeofenceHelper.getIconLayer(geofenceType.FINISH_ALLOW), caption: 'Parking allowed'},
    {id: geofenceType.FINISH_RESTRICT, iconClass: GeofenceHelper.getIconLayer(geofenceType.FINISH_RESTRICT), caption: 'Parking prohibited'},
    {id: geofenceType.SPEED_LIMIT, iconClass: GeofenceHelper.getIconLayer(geofenceType.SPEED_LIMIT), caption: 'Speed limit'},
  ];

  constructor(public mapLayersService: MapLayersService) {
  }

  itemValues = new BehaviorSubject<ItemValues>({
    regions: false,
    sum_of_geofences: false,
    demand: false,
    tech_work_area: false,
    geopoints: false,
    ride_allow: false,
    ride_restrict: false,
    finish_allow: false,
    finish_restrict: false,
    speed_limit: false,
  });
  geofenceTypes = [
    geofenceType.RIDE_ALLOW,
    geofenceType.RIDE_RESTRICT,
    geofenceType.FINISH_ALLOW,
    geofenceType.FINISH_RESTRICT,
    geofenceType.SPEED_LIMIT,
  ];

  ngOnInit() {
    this.updateFilteredItems();
    this.initState();
    this.subscribeState();
  }

  initVisibleItemsState() {

    const values = {};

    this.items.forEach(item => {
      values[item.id] = this.visibleItems.includes(item.id);
    });

    this.itemValues.next(values);
  }

  updateFilteredItems() {

    if (!this.visibleItems || this.visibleItems.length === 0) {
      this.filteredItems = this.items;
      return;
    }

    this.filteredItems = this.items.filter(item =>
      this.visibleItems.includes(item.id)
    );
  }

  initState() {
    const hasVisibleItems =
      this.visibleItems &&
      this.visibleItems.length;

    if (!this.useStorage) {
      if (hasVisibleItems) {
        this.initVisibleItemsState();
      }
      return;
    }

    const stored = localStorage.getItem(GEO_ITEM_TOGGLE_STORAGE_KEY);

    if (!stored) {
      if (hasVisibleItems) {
        this.initVisibleItemsState();
      }
      return;
    }

    let parsed = JSON.parse(stored);

    if (hasVisibleItems) {
      parsed = this.normalizeByVisibleItems(parsed);
    }

    this.itemValues.next({
      ...this.itemValues.getValue(),
      ...parsed
    });
  }

  normalizeByVisibleItems(parsed: Record<string, boolean>) {
    Object.keys(parsed).forEach(key => {
      if (!this.visibleItems.includes(key as GeoItemId)) {
        parsed[key] = false;
      }
    });

    return parsed;
  }

  subscribeState() {
    this.itemValues.subscribe(values => {
      this.geofenceTypeCountBlockVisibility =
        this.geofenceTypes.some(type => values[type]) ||
        values.geofences;

      if (this.useStorage) {
        localStorage.setItem(
          GEO_ITEM_TOGGLE_STORAGE_KEY,
          JSON.stringify(values)
        );
      }

      this.mapLayersService.geoitemVisibility.next(values);
    });
  }

  toggle(itemValues: ItemValues, id: GeoItemId) {
    const newValues = Object.assign({}, itemValues);
    newValues[id] = !newValues[id];
    if (id === 'geofences' && newValues[id]) {
      this.geofenceTypeCountBlockVisibility = true;
    }
    if (id === 'geofences' && !newValues[id]) {
      this.geofenceTypeCountBlockVisibility = false;
      this.geofenceTypes.forEach(type => newValues[type] = false);
    }
    this.itemValues.next(newValues);
  }

  getVisibilityIconClass(itemValues: ItemValues, id: GeoItemId): string {
    if (this.geofenceTypes.includes(id)) {
      return itemValues[id] ? 'icon-checkbox-on' : 'icon-checkbox-off';
    } else {
      return itemValues[id] ? 'icon-toggle-on' : 'icon-toggle-off';
    }
  }
}
